import { racerDataCache } from "./racerDataCache";
import type { ParticipantListOrder } from "../types/participantListOrder";
import { retryRaceCallable } from "./raceCallableRetry";
import type { OrganizerCourseUpdates } from "../types/organizerCourse";
import type { RaceRule } from "../utils/raceRules";
import { httpsCallable } from "firebase/functions";
import type { RaceClockServerSample } from "./raceClock";
import { functions } from "../firebase";
import { parseUserRace } from "../utils/iosModelParsers";
import { parsePlateNumber } from "../utils/racePlate";
import { getCurrentLocale, type LocaleCode } from "../localization";
import type { RaceInsuranceCertificate, RaceInsuranceInput } from "../types/raceInsurance";
import type { VenueAmenity } from "../types/venueAmenities";
import { parseOrganizerRaceTaxSubmission, parseOrganizerTaxSubmissionInbox, type OrganizerTaxSubmissionInbox, type OrganizerRaceTaxSubmission, type OrganizerTaxSubmissionDraft, type OrganizerTaxSubmissionMutation } from "../types/organizerRaceTaxSubmission";
import { parseRaceTaxReadiness, type RaceTaxReadiness } from "../types/raceTaxReadiness";
import { parseAdminRaceTaxReview, type AdminRaceTaxReview, type AdminRaceTaxReviewDraft, type AdminRaceTaxReviewExpected, type TaxReviewAddress } from "../types/adminRaceTaxReview";
import type {
  GearItem,
  RegistrationPricingSnapshot,
  Sponsor,
  UserRace,
} from "../types/iosModels";
import {
  parseCurrentRegistrationPricingSnapshot,
  registrationPricingSnapshotsMatch,
  type RegistrationPricingAcknowledgement,
} from "../utils/registrationPricing";
import type {
  CreateRaceDraftInput,
  PaidRaceAccessStatus,
  PaidRaceApplicationInput,
} from "./firestoreService";

type StripeConnectStatusResponse = {
  accountId: string | null;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  disabledReason: string | null;
};

export type OrganizerStripeTaxRegistration = {
  id: string;
  country: string;
  state: string | null;
  type: string | null;
  jurisdiction?: string | null;
  expiresAt: string | null;
};

export type OrganizerStripeTaxStatus = {
  settingsStatus: "active" | "pending" | "unavailable";
  missingFields: string[];
  activeRegistrations: OrganizerStripeTaxRegistration[];
  ready: boolean;
  checkedAt: string | null;
};

const asFunctionsRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const parseOrganizerStripeTaxStatus = (
  value: unknown
): OrganizerStripeTaxStatus => {
  const record = asFunctionsRecord(value);
  const rawStatus = record.settingsStatus;
  const settingsStatus =
    rawStatus === "active" || rawStatus === "pending"
      ? rawStatus
      : "unavailable";
  const missingFields = Array.isArray(record.missingFields)
    ? record.missingFields.filter(
        (entry): entry is string => typeof entry === "string" && Boolean(entry.trim())
      )
    : [];
  const activeRegistrations = Array.isArray(record.activeRegistrations)
    ? record.activeRegistrations.flatMap((value) => {
        const registration = asFunctionsRecord(value);
        if (
          typeof registration.id !== "string" ||
          !registration.id.trim() ||
          typeof registration.country !== "string" ||
          !registration.country.trim()
        ) {
          return [];
        }
        return [
          {
            id: registration.id,
            country: registration.country.toUpperCase(),
            state:
              typeof registration.state === "string" && registration.state.trim()
                ? registration.state
                : null,
            type:
              typeof registration.type === "string" && registration.type.trim()
                ? registration.type
                : null,
            jurisdiction:
              typeof registration.jurisdiction === "string" &&
              registration.jurisdiction.trim()
                ? registration.jurisdiction.trim()
                : null,
            expiresAt:
              typeof registration.expiresAt === "string" &&
              registration.expiresAt.trim()
                ? registration.expiresAt
                : null,
          },
        ];
      })
    : [];

  return {
    settingsStatus,
    missingFields,
    activeRegistrations,
    ready: record.ready === true,
    checkedAt:
      typeof record.checkedAt === "string" && record.checkedAt.trim()
        ? record.checkedAt
        : null,
  };
};

export type RacePayoutBlocker = {
  code: string;
  message: string;
  registrationId: string | null;
};

export type RacePayoutRegistrationCounts = {
  total: number;
  held: number;
  transferred: number;
  reversed: number;
  refunded: number;
  disputed: number;
  ineligible: number;
};

export type RacePayoutCurrencyTotals = {
  /** Stripe minor units for the currency key. */
  gross: number;
  refunded: number;
  platformFee: number;
  organizerPayable: number;
  transferred: number;
  reversed: number;
  baseEntryAmount?: number;
  racerServiceFeeAmount?: number;
  totalAmount?: number;
  platformCommissionAmount?: number;
  organizerTransferAmount?: number;
  organizerProceedsPayable?: number;
  organizerTaxPayable?: number;
  platformRetainedAmount?: number;
  refundedBaseEntryAmount?: number;
  refundedRacerServiceFeeAmount?: number;
};

export type RacePayoutHoldWindow = {
  earliestHoldDeadlineAt: Date | null;
  payoutEligibleAt: Date | null;
  releaseBufferHours: number | null;
  remainingHours: number | null;
  urgent: boolean;
};

export type RacePayoutInspection = {
  raceId: string;
  paymentFlowVersion: string | null;
  releasable: boolean;
  blockers: RacePayoutBlocker[];
  organizer: {
    organizerId: string | null;
    stripeAccountId: string | null;
    ready: boolean;
  };
  holdWindow: RacePayoutHoldWindow;
  registrations: RacePayoutRegistrationCounts;
  totalsByCurrency: Record<string, RacePayoutCurrencyTotals>;
};

export type RacePayoutReleaseTransfer = {
  registrationId: string | null;
  transferId: string | null;
  currency: string | null;
  amount: number | null;
  status: string | null;
};

export const RACE_PAYOUT_RELEASE_STATUSES = [
  "released",
  "already_released",
  "partially_released",
] as const;

export type RacePayoutReleaseStatus =
  | (typeof RACE_PAYOUT_RELEASE_STATUSES)[number]
  | "unknown";

export type RacePayoutReleaseCounts = {
  createdCount: number;
  alreadyTransferredCount: number;
  skippedCount: number;
  failedCount: number;
  processingCount: number;
  reconciledCount: number;
  [key: string]: number;
};

export type RacePayoutReleaseResult = {
  raceId: string;
  status: RacePayoutReleaseStatus;
  createdCount: number;
  alreadyTransferredCount: number;
  skippedCount: number;
  failedCount: number;
  processingCount: number;
  reconciledCount: number;
  /** Compatibility view for callers that consumed the earlier nested shape. */
  counts: RacePayoutReleaseCounts;
  transfers: RacePayoutReleaseTransfer[];
  totalsByCurrency: Record<string, RacePayoutCurrencyTotals>;
};

export type PublicRacerProfile = {
  id: string;
  userName: string;
  bio: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  profilePhotoURLString: string | null;
  isRacing: boolean;
  isBannedFromRacing: boolean;
  totalDistanceRaced: number;
  totalRacesFinished: number;
  totalRacesWon: number;
  registeredRacesCount: number;
  createdRacesCount: number;
  registrationDate: Date | null;
  gear: GearItem[];
};

export type RegisteredRacer = PublicRacerProfile & {
  raceRegistrationDate: Date | null;
  plateNumber: number | null;
  category: string | null;
};

export type RaceParticipantSearchResult = {
  id: string;
  userName: string;
  email: string | null;
  registrationId: string | null;
  userRaceId: string | null;
};

export type RaceDisqualificationNotificationStatus =
  | "sent"
  | "skipped_missing_token"
  | "skipped_invalid_token"
  | "skipped_error";

type RawPublicRacerProfile = Omit<PublicRacerProfile, "registrationDate"> & {
  registrationDate: string | null;
};

type RawRegisteredRacer = RawPublicRacerProfile & {
  raceRegistrationDate: string | null;
  plateNumber?: number | null;
  category?: string | null;
};

export type SupportRequestInput = {
  name: string;
  email: string;
  category: string;
  subject?: string;
  message: string;
  pageUrl?: string;
  userAgent?: string;
  company?: string;
};

export type MarketingSubscriptionStatus =
  | "ignored"
  | "pending_provider_configuration"
  | "pending_double_opt_in"
  | "provider_error"
  | "subscribed";

export type MarketingRacePreferences = {
  schemaVersion: 2;
  sport: "any" | "running" | "cycling";
  formatKind: "any" | "startToFinish" | "checkpoints";
  cityOrRegion: string;
};

export type MarketingSubscriptionInput = {
  email: string;
  marketingConsent: boolean;
  consentText: string;
  consentVersion: string;
  source: string;
  pageUrl?: string;
  userAgent?: string;
  company?: string;
  racePreferences?: MarketingRacePreferences;
};

export type TestRaceDebugCleanupSummary = {
  participantsFound: number;
  usersUpdated: number;
  activeRacesCleared: number;
  registrations: number;
  userRaces: number;
  userRaceGpxFiles: number;
  raceResults: number;
  racersLocations: number;
  disqualifications: number;
  cancellationDeliveries: number;
  pushDeliveryAttempts: number;
  pushDeliveryJobs: number;
  stripeCheckoutSessionsExpired: number;
  stripePaymentIntentsCanceled: number;
  stripePaymentIntentsRefunded: number;
  stripePendingObjectsSkipped: number;
};

export type TestRaceDebugReset = {
  status: "in_progress" | "failed" | "complete";
  generation: number;
  operationId: string | null;
  cleanup: TestRaceDebugCleanupSummary | null;
};

export type TestRaceDebugDocumentResponse = {
  raceId: string;
  document: Record<string, unknown>;
  revision: string | null;
  exists: boolean;
  reset: TestRaceDebugReset;
};

export type TestRaceDebugPresetId =
  | "freeScheduledCheckpoints"
  | "paidScheduledCheckpoints"
  | "freeScheduledStartToFinish"
  | "paidScheduledStartToFinish"
  | "freeUserInitiatedStartToFinish"
  | "paidUserInitiatedStartToFinish";

export type TestRaceDebugSaveInput = {
  resetState: boolean;
  revision: string | null;
} & ({ document: Record<string, unknown>; presetId?: never } | {
  document?: never;
  presetId: TestRaceDebugPresetId;
});

export type ConversionEventInput = {
  eventName: string;
  pagePath: string;
  sessionId: string;
  raceId?: string;
  cta?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  referrerHost?: string;
  analyticsConsent: true;
  consentVersion: string;
};

export type AdminConversionAnalyticsDays = 7 | 30 | 90 | 180;

export type AdminConversionAnalytics = {
  days: AdminConversionAnalyticsDays;
  rangeStart: string;
  rangeEnd: string;
  generatedAt: string;
  coverage: {
    totalEvents: number;
    uniqueSessions: number;
    firstEventAt: string | null;
    lastEventAt: string | null;
  };
  overview: {
    pageViews: number;
    publicRaceViewSessions: number;
    registrationClickSessions: number;
    registrationCompletionSessions: number;
    publicRaceShareSessions: number;
    organizerCtaSessions: number;
    organizerActivationSessions: number;
  };
  participantFunnel: {
    raceViewSessions: number;
    registrationClickSessions: number;
    registrationCompletionSessions: number;
  };
  organizerFunnel: {
    organizePageSessions: number;
    pricingViewSessions: number;
    ctaClickSessions: number;
    activationSessions: number;
  };
  daily: Array<{
    date: string;
    publicRaceViews: number;
    registrationClicks: number;
    registrationCompletions: number;
    organizerCtaClicks: number;
    organizerActivations: number;
  }>;
  acquisition: Array<{
    source: string;
    medium: string | null;
    campaign: string | null;
    referrerHost: string | null;
    sessions: number;
  }>;
  organizerCtas: Array<{
    cta: string;
    sessions: number;
  }>;
  topRaces: Array<{
    raceId: string;
    raceName: string | null;
    viewSessions: number;
    clickSessions: number;
    completionSessions: number;
    shareSessions: number;
  }>;
};

const parseCallableDate = (value: unknown): Date | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const asCallableRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const parseCallableString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized || null;
};

const parseCallableBoolean = (value: unknown): boolean =>
  value === true || (typeof value === "string" && value.trim().toLowerCase() === "true");

const parseCallableCount = (value: unknown): number => {
  const numericValue = typeof value === "string" ? Number(value) : value;
  return typeof numericValue === "number" && Number.isFinite(numericValue)
    ? Math.max(0, Math.trunc(numericValue))
    : 0;
};

const parseCallableInteger = (value: unknown): number | null => {
  const numericValue = typeof value === "string" ? Number(value) : value;
  return typeof numericValue === "number" && Number.isFinite(numericValue)
    ? Math.trunc(numericValue)
    : null;
};

const parsePayoutCurrencyTotals = (
  value: unknown
): Record<string, RacePayoutCurrencyTotals> => {
  const totalsRecord = asCallableRecord(value);

  return Object.fromEntries(
    Object.entries(totalsRecord)
      .map(
        ([currency, rawTotals]): readonly [
          string,
          RacePayoutCurrencyTotals
        ] | null => {
        const normalizedCurrency = currency.trim().toUpperCase();
        if (!/^[A-Z]{3}$/.test(normalizedCurrency)) {
          return null;
        }

        const totals = asCallableRecord(rawTotals);
        const gross = parseCallableCount(totals.gross);
        const platformFee = parseCallableCount(totals.platformFee);
        const organizerPayable = parseCallableCount(totals.organizerPayable);
        const racerServiceFeeAmount = parseCallableCount(
          totals.racerServiceFeeAmount
        );
        return [
          normalizedCurrency,
          {
            gross,
            refunded: parseCallableCount(totals.refunded),
            platformFee,
            organizerPayable,
            transferred: parseCallableCount(totals.transferred),
            reversed: parseCallableCount(totals.reversed),
            baseEntryAmount:
              parseCallableInteger(totals.baseEntryAmount) ??
              Math.max(gross - racerServiceFeeAmount, 0),
            racerServiceFeeAmount,
            totalAmount: parseCallableInteger(totals.totalAmount) ?? gross,
            platformCommissionAmount:
              parseCallableInteger(totals.platformCommissionAmount) ?? platformFee,
            organizerTransferAmount:
              parseCallableInteger(totals.organizerTransferAmount) ??
              organizerPayable,
            organizerProceedsPayable:
              parseCallableInteger(totals.organizerProceedsPayable) ??
              organizerPayable,
            organizerTaxPayable:
              parseCallableInteger(totals.organizerTaxPayable) ?? 0,
            platformRetainedAmount:
              parseCallableInteger(totals.platformRetainedAmount) ??
              platformFee + racerServiceFeeAmount,
            refundedBaseEntryAmount: parseCallableCount(
              totals.refundedBaseEntryAmount
            ),
            refundedRacerServiceFeeAmount: parseCallableCount(
              totals.refundedRacerServiceFeeAmount
            ),
          },
        ] as const;
        }
      )
      .filter(
        (entry): entry is readonly [string, RacePayoutCurrencyTotals] => entry !== null
      )
  );
};

export const parseRacePayoutInspection = (
  value: unknown,
  requestedRaceId: string
): RacePayoutInspection => {
  const outerRecord = asCallableRecord(value);
  const record = Object.keys(asCallableRecord(outerRecord.inspection)).length
    ? asCallableRecord(outerRecord.inspection)
    : outerRecord;
  const organizer = asCallableRecord(record.organizer);
  const holdWindow = asCallableRecord(record.holdWindow);
  const registrations = asCallableRecord(record.registrations);
  const rawBlockers = Array.isArray(record.blockers) ? record.blockers : [];

  return {
    raceId: parseCallableString(record.raceId) || requestedRaceId,
    paymentFlowVersion: parseCallableString(record.paymentFlowVersion),
    releasable: parseCallableBoolean(record.releasable),
    blockers: rawBlockers
      .map((rawBlocker): RacePayoutBlocker | null => {
        if (typeof rawBlocker === "string") {
          return {
            code: "release_blocked",
            message: rawBlocker.trim() || "Payout release is blocked.",
            registrationId: null,
          };
        }

        const blocker = asCallableRecord(rawBlocker);
        const message = parseCallableString(blocker.message);
        if (!message) {
          return null;
        }

        return {
          code: parseCallableString(blocker.code) || "release_blocked",
          message,
          registrationId: parseCallableString(blocker.registrationId),
        };
      })
      .filter((blocker): blocker is RacePayoutBlocker => blocker !== null),
    organizer: {
      organizerId: parseCallableString(organizer.organizerId),
      stripeAccountId: parseCallableString(organizer.stripeAccountId),
      ready: parseCallableBoolean(organizer.ready),
    },
    holdWindow: {
      earliestHoldDeadlineAt: parseCallableDate(holdWindow.earliestHoldDeadlineAt),
      payoutEligibleAt: parseCallableDate(holdWindow.payoutEligibleAt),
      releaseBufferHours: parseCallableInteger(holdWindow.releaseBufferHours),
      remainingHours: parseCallableInteger(holdWindow.remainingHours),
      urgent: parseCallableBoolean(holdWindow.urgent),
    },
    registrations: {
      total: parseCallableCount(registrations.total),
      held: parseCallableCount(registrations.held),
      transferred: parseCallableCount(registrations.transferred),
      reversed: parseCallableCount(registrations.reversed),
      refunded: parseCallableCount(registrations.refunded),
      disputed: parseCallableCount(registrations.disputed),
      ineligible: parseCallableCount(registrations.ineligible),
    },
    totalsByCurrency: parsePayoutCurrencyTotals(
      record.totalsByCurrency || record.totals
    ),
  };
};

export const parseRacePayoutReleaseResult = (
  value: unknown,
  requestedRaceId: string
): RacePayoutReleaseResult => {
  const outerRecord = asCallableRecord(value);
  const record = Object.keys(asCallableRecord(outerRecord.release)).length
    ? asCallableRecord(outerRecord.release)
    : outerRecord;
  const rawCounts = asCallableRecord(record.counts);
  const rawTransfers = Array.isArray(record.transfers) ? record.transfers : [];
  const countKeys = [
    "createdCount",
    "alreadyTransferredCount",
    "skippedCount",
    "failedCount",
    "processingCount",
    "reconciledCount",
  ] as const;
  const nestedCountAliases: Record<(typeof countKeys)[number], string> = {
    createdCount: "created",
    alreadyTransferredCount: "alreadyTransferred",
    skippedCount: "skipped",
    failedCount: "failed",
    processingCount: "processing",
    reconciledCount: "reconciled",
  };
  const counts = {
    ...Object.fromEntries(
      Object.entries(rawCounts).map(([key, count]) => [key, parseCallableCount(count)])
    ),
    ...Object.fromEntries(
      countKeys.map((key) => [
        key,
        parseCallableCount(
          record[key] !== undefined && record[key] !== null
            ? record[key]
            : rawCounts[key] !== undefined && rawCounts[key] !== null
              ? rawCounts[key]
              : rawCounts[nestedCountAliases[key]]
        ),
      ])
    ),
  } as RacePayoutReleaseCounts;
  const rawStatus = parseCallableString(record.status);
  const status = RACE_PAYOUT_RELEASE_STATUSES.includes(
    rawStatus as (typeof RACE_PAYOUT_RELEASE_STATUSES)[number]
  )
    ? (rawStatus as (typeof RACE_PAYOUT_RELEASE_STATUSES)[number])
    : "unknown";

  return {
    raceId: parseCallableString(record.raceId) || requestedRaceId,
    status,
    createdCount: counts.createdCount,
    alreadyTransferredCount: counts.alreadyTransferredCount,
    skippedCount: counts.skippedCount,
    failedCount: counts.failedCount,
    processingCount: counts.processingCount,
    reconciledCount: counts.reconciledCount,
    counts,
    transfers: rawTransfers.map((rawTransfer) => {
      const transfer = asCallableRecord(rawTransfer);
      return {
        registrationId: parseCallableString(transfer.registrationId),
        transferId:
          parseCallableString(transfer.transferId) || parseCallableString(transfer.id),
        currency: parseCallableString(transfer.currency)?.toUpperCase() || null,
        amount:
          transfer.amount === null || transfer.amount === undefined
            ? null
            : parseCallableCount(transfer.amount),
        status: parseCallableString(transfer.status),
      };
    }),
    totalsByCurrency: parsePayoutCurrencyTotals(
      record.totalsByCurrency || record.totals
    ),
  };
};

export type RaceRegistrationCheckoutResponse = {
  checkoutUrl: string;
  registrationId: string;
  livemode: boolean;
  amount: number;
  currency: string;
  pricing: RegistrationPricingSnapshot;
};

export const parseRaceRegistrationCheckoutResponse = (
  value: unknown,
  acknowledgement: RegistrationPricingAcknowledgement,
  expectedPricing: RegistrationPricingSnapshot
): RaceRegistrationCheckoutResponse => {
  const record = asCallableRecord(value);
  const checkoutUrl = parseCallableString(record.checkoutUrl);
  const registrationId = parseCallableString(record.registrationId);
  const amount = record.amount;
  const currency = parseCallableString(record.currency);
  const pricing = parseCurrentRegistrationPricingSnapshot(record.pricing);
  let parsedCheckoutUrl: URL | null = null;
  try {
    parsedCheckoutUrl = checkoutUrl ? new URL(checkoutUrl) : null;
  } catch {
    parsedCheckoutUrl = null;
  }

  if (
    !checkoutUrl ||
    !parsedCheckoutUrl ||
    parsedCheckoutUrl.protocol !== "https:" ||
    !registrationId ||
    typeof amount !== "number" ||
    !Number.isSafeInteger(amount) ||
    amount <= 0 ||
    !currency ||
    currency !== currency.trim().toUpperCase() ||
    !pricing ||
    typeof record.livemode !== "boolean"
  ) {
    throw new Error("Backend returned an invalid registration checkout quote.");
  }

  if (
    acknowledgement.feePolicyVersion !== expectedPricing.feePolicyVersion ||
    acknowledgement.pricingDisclosureVersion !==
      expectedPricing.pricingDisclosureVersion ||
    acknowledgement.totalAmount !== expectedPricing.totalAmount ||
    acknowledgement.currency !== expectedPricing.currency ||
    amount !== acknowledgement.totalAmount ||
    currency !== acknowledgement.currency ||
    pricing.totalAmount !== amount ||
    pricing.currency !== currency ||
    !registrationPricingSnapshotsMatch(pricing, expectedPricing)
  ) {
    throw new Error(
      "The registration total changed. Review the updated race price before paying."
    );
  }

  return {
    checkoutUrl,
    registrationId,
    livemode: record.livemode,
    amount,
    currency,
    pricing,
  };
};

const serializeCallableValue = (value: unknown): unknown => {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map((entry) => serializeCallableValue(entry));
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .map(([key, entry]) => [key, serializeCallableValue(entry)])
  );
};

const rawRaceFormatFromUserRace = (
  format: UserRace["format"]
): Record<string, unknown> | null => {
  if (format.raw && Object.keys(format.raw).length > 0) {
    return format.raw;
  }

  switch (format.kind) {
    case "endurance":
      return format.enduranceType ? { type: format.enduranceType } : null;
    case "startToFinish":
      return { startToFinish: true };
    case "enduro":
      return { enduro: true };
    case "downhill":
      return { downhill: true };
    case "laps":
      return { laps: true };
    case "checkpoints":
      return { checkpoints: true };
    case "scavengerHunt":
      return { scavengerHunt: true };
    case "globalOpenTimeTrial":
      return { globalOpenTimeTrial: true };
    case "openCourseRacing":
      return { openCourseRacing: true };
    default:
      return null;
  }
};

const serializeUserRaceForCallable = (userRace: UserRace): Record<string, unknown> =>
  serializeCallableValue({
    ...userRace,
    format: rawRaceFormatFromUserRace(userRace.format),
  }) as Record<string, unknown>;

const parsePublicRacerProfile = (
  profile: RawPublicRacerProfile
): PublicRacerProfile => ({
  ...profile,
  registrationDate: parseCallableDate(profile.registrationDate),
});

const parseRegisteredRacer = (racer: RawRegisteredRacer): RegisteredRacer => ({
  ...parsePublicRacerProfile(racer),
  raceRegistrationDate: parseCallableDate(racer.raceRegistrationDate),
  plateNumber: parsePlateNumber(racer.plateNumber),
  category: typeof racer.category === "string" && racer.category.trim() !== "Undefined" ? racer.category.trim() || null : null,
});

class FunctionsService {
  async syncVerifiedAccountEmail(): Promise<{ email: string }> {
    const call = httpsCallable<{ locale: LocaleCode }, { email: string }>(functions, "syncVerifiedAccountEmail");
    return (await call({ locale: getCurrentLocale() })).data;
  }
  async trackConversionEvent(input: ConversionEventInput): Promise<{ ok: boolean }> {
    const callable = httpsCallable<ConversionEventInput, { ok: boolean }>(
      functions,
      "trackConversionEvent"
    );
    const result = await callable(input);
    return result.data;
  }

  async getAdminConversionAnalytics(
    days: AdminConversionAnalyticsDays
  ): Promise<AdminConversionAnalytics> {
    const callable = httpsCallable<
      { days: AdminConversionAnalyticsDays },
      AdminConversionAnalytics
    >(functions, "getAdminConversionAnalytics");
    const result = await callable({ days });
    return result.data;
  }

  async getRaceClockSample(): Promise<RaceClockServerSample> {
    const callable = httpsCallable<undefined, RaceClockServerSample>(
      functions,
      "getRaceClockSample"
    );
    const result = await callable();
    return result.data;
  }

  async subscribeToMarketingList(input: MarketingSubscriptionInput): Promise<{
    ok: boolean;
    status: MarketingSubscriptionStatus;
    stored?: boolean;
  }> {
    const callable = httpsCallable<
      MarketingSubscriptionInput,
      { ok: boolean; status: MarketingSubscriptionStatus; stored?: boolean }
    >(functions, "subscribeToMarketingList");
    const result = await callable(input);
    return result.data;
  }

  async submitSupportRequest(
    input: SupportRequestInput
  ): Promise<{ ok: boolean; requestId: string | null }> {
    const callable = httpsCallable<
      SupportRequestInput,
      { ok: boolean; requestId: string | null }
    >(functions, "submitSupportRequest");
    const result = await callable(input);
    return result.data;
  }

  async updateRaceInsuranceCertificate(input: {
    raceId: string;
    certificate: RaceInsuranceCertificate;
  }): Promise<{ raceId: string; certificate: RaceInsuranceCertificate }> {
    const callable = httpsCallable<typeof input, { raceId: string; certificate: RaceInsuranceCertificate }>(
      functions, "updateRaceInsuranceCertificate"
    );
    return (await callable(input)).data;
  }

  async acceptRaceInsurance(input: {
    raceId: string;
    insurance: RaceInsuranceInput;
  }): Promise<{ raceId: string; publishBlockReason: string | null }> {
    const callable = httpsCallable<
      { raceId: string; insurance: RaceInsuranceInput },
      { raceId: string; publishBlockReason: string | null }
    >(functions, "createRaceSubmission");
    const result = await callable(input);
    return result.data;
  }

  async createRaceSubmission(input: CreateRaceDraftInput): Promise<{
    raceId: string;
    publishBlockReason: string | null;
  }> {
    const callable = httpsCallable<
      {
        insurance: CreateRaceDraftInput["insurance"];
        requireParticipantId?: boolean;
        raceId?: string;
        organizerUserId: string;
        organizerName: string;
        organizerEmail?: string | null;
        organizerContact?: {
          name: string;
          email: string;
          phone: string;
          address: string;
          city: string;
          country: string;
          websiteUrl: string;
          socialUrl: string;
          organizationName: string;
        };
        notificationEmail: string;
        emailSubject: string;
        notifyOrganizerOnRegistration?: boolean;
        emailBody: string;
        organizerPhotoURL?: string | null;
        name: string;
        shortDescription: string;
        description: string;
        city: string;
        state?: string | null;
        province?: string | null;
        country: string;
        address: string;
        postalCode?: string | null;
        eventTimeZone: string;
        sportType: string;
        difficulty: string;
        startPolicy: string;
        startDate: string;
        endDate: string;
        eventStartDate?: string;
        eventEndDate?: string;
        effectiveOpenWindowStart?: string | null;
        effectiveOpenWindowEnd?: string | null;
        registrationOpenDate: string;
        registrationCloseDate: string;
        entryType: string;
        price: string;
        currency: string;
        refundPolicyType?: string | null;
        refundDeadline?: string | null;
        isRefundable?: boolean;
        maxAllowedParticipants?: number | null;
        mobileCoverImageUrl?: string | null;
        webCoverImageUrl?: string | null;
        photosFirebaseURLs?: string[];
        sponsors?: unknown[];
        venueAmenities?: VenueAmenity[];
        format: unknown;
        totalLaps?: number | null;
        gpxFileFirebaseURLString?: string | null;
        startLocation?: unknown;
        endLocation?: unknown;
        checkInLocation?: unknown;
        checkInStartTime?: string | null;
        checkInCloseTime?: string | null;
        routePolyline?: unknown[];
        checkpoints?: unknown[];
        aidStations?: unknown[];
        raceCues?: unknown[];
        proximitySettings: CreateRaceDraftInput["proximitySettings"];
        categories?: unknown[];
        prizes?: string[];
        waiver?: string;
        raceMeetingTime?: string | null;
        podiumCeremonyDate?: string | null;
        length?: number | null;
        surfaceType?: string;
        totalAscent?: number | null;
        totalDescent?: number | null;
        targetDistance?: number | null;
        isCheckInEnabled?: boolean | null;
        isFlexibleRoute?: boolean;
        allowsReEntry?: boolean;
        minimumFinishTimeSeconds?: number;
        offlineResultSyncGraceHours?: number;
        enforceOffRoute?: boolean;
        courseVerificationEnabled?: boolean;
        showsRacersLocations?: boolean;
        shouldBypassLocationCheckIn?: boolean;
        rules?: string[];
        ruleDetails?: RaceRule[];
        isDiscoverable?: boolean;
        isTest?: boolean;
      },
      { raceId: string; publishBlockReason: string | null }
    >(functions, "createRaceSubmission");

    const request = {
      ...input,
      raceId: input.raceId || crypto.randomUUID(),
      eventStartDate: (input.eventStartDate || input.startDate).toISOString(),
      eventEndDate: (input.eventEndDate || input.endDate).toISOString(),
      startDate: input.startDate.toISOString(),
      endDate: input.endDate.toISOString(),
      effectiveOpenWindowStart: input.effectiveOpenWindowStart
        ? input.effectiveOpenWindowStart.toISOString()
        : null,
      effectiveOpenWindowEnd: input.effectiveOpenWindowEnd
        ? input.effectiveOpenWindowEnd.toISOString()
        : null,
      registrationOpenDate: input.registrationOpenDate.toISOString(),
      registrationCloseDate: input.registrationCloseDate.toISOString(),
      refundDeadline: input.refundDeadline ? input.refundDeadline.toISOString() : null,
      checkInStartTime: input.checkInStartTime
        ? input.checkInStartTime.toISOString()
        : null,
      checkInCloseTime: input.checkInCloseTime
        ? input.checkInCloseTime.toISOString()
        : null,
      raceMeetingTime: input.raceMeetingTime ? input.raceMeetingTime.toISOString() : null,
      podiumCeremonyDate: input.podiumCeremonyDate
        ? input.podiumCeremonyDate.toISOString()
        : null,
    };
    const result = await retryRaceCallable(() => callable(request));

    return result.data;
  }

  async startOrganizerStripeOnboarding(): Promise<{
    accountId: string;
    onboardingUrl: string;
  }> {
    const callable = httpsCallable<undefined, { accountId: string; onboardingUrl: string }>(
      functions,
      "startOrganizerStripeOnboarding"
    );
    const result = await callable(undefined);
    return result.data;
  }

  async ensureOrganizerFinanceSummary(): Promise<{
    status: "complete" | "in_progress";
    scannedRegistrations?: number;
  }> {
    const callable = httpsCallable<
      undefined,
      {
        status: "complete" | "in_progress";
        scannedRegistrations?: number;
      }
    >(functions, "ensureOrganizerFinanceSummary");
    const result = await callable(undefined);
    return result.data;
  }

  async updateOrganizerRace(input: {
    raceId: string;
    updates: OrganizerCourseUpdates & {
      requireParticipantId?: boolean;
      name?: string;
      shortDescription?: string;
      description?: string;
      notificationEmail?: string;
      emailSubject?: string;
      notifyOrganizerOnRegistration?: boolean;
      emailBody?: string;
      rules?: string[];
      ruleDetails?: RaceRule[];
      isDiscoverable?: boolean;
      startDate?: string;
      endDate?: string;
      effectiveOpenWindowStart?: string;
      effectiveOpenWindowEnd?: string;
      eventStartDate?: string;
      eventEndDate?: string;
      mobileCoverImageUrl?: string;
      webCoverImageUrl?: string;
      photosFirebaseURLs?: string[];
      sponsors?: Sponsor[];
      venueAmenities?: VenueAmenity[];
      prizes?: string[];
      minimumFinishTimeSeconds?: number;
      offlineResultSyncGraceHours?: number;
    };
  }): Promise<void> {
    const callable = httpsCallable<
      {
        raceId: string;
        updates: OrganizerCourseUpdates & {
          requireParticipantId?: boolean;
          name?: string;
          shortDescription?: string;
          description?: string;
          notificationEmail?: string;
          emailSubject?: string;
          notifyOrganizerOnRegistration?: boolean;
          emailBody?: string;
          rules?: string[];
          ruleDetails?: RaceRule[];
          isDiscoverable?: boolean;
          startDate?: string;
          endDate?: string;
          effectiveOpenWindowStart?: string;
          effectiveOpenWindowEnd?: string;
          eventStartDate?: string;
          eventEndDate?: string;
          mobileCoverImageUrl?: string;
          webCoverImageUrl?: string;
          photosFirebaseURLs?: string[];
          sponsors?: Sponsor[];
          venueAmenities?: VenueAmenity[];
          prizes?: string[];
          minimumFinishTimeSeconds?: number;
          offlineResultSyncGraceHours?: number;
        };
      },
      { ok: boolean }
    >(functions, "updateOrganizerRace");

    await callable(input);
  }

  async cancelOrganizerRace(input: { raceId: string }): Promise<void> {
    const callable = httpsCallable<{ raceId: string }, { ok: boolean }>(
      functions,
      "cancelOrganizerRace"
    );

    await callable(input);
  }

  async refreshOrganizerStripeStatus(): Promise<{
    stripeConnect: StripeConnectStatusResponse | null;
  }> {
    const callable = httpsCallable<
      undefined,
      { stripeConnect: StripeConnectStatusResponse | null }
    >(functions, "refreshOrganizerStripeStatus");
    const result = await callable(undefined);
    return result.data;
  }

  async createOrganizerStripeTaxAccountSession(): Promise<{
    clientSecret: string;
  }> {
    const callable = httpsCallable<undefined, unknown>(
      functions,
      "createOrganizerStripeTaxAccountSession"
    );
    const result = await callable(undefined);
    const record = asFunctionsRecord(result.data);
    if (typeof record.clientSecret !== "string" || !record.clientSecret.trim()) {
      throw new Error("Stripe did not return a usable tax setup session.");
    }
    return { clientSecret: record.clientSecret };
  }

  async getOrganizerStripeTaxStatus(): Promise<OrganizerStripeTaxStatus> {
    const callable = httpsCallable<undefined, unknown>(
      functions,
      "getOrganizerStripeTaxStatus"
    );
    const result = await callable(undefined);
    return parseOrganizerStripeTaxStatus(result.data);
  }

  async getRaceTaxReadiness(input: { raceId: string }): Promise<RaceTaxReadiness> {
    const callable = httpsCallable<{ raceId: string }, unknown>(
      functions,
      "getRaceTaxReadiness"
    );
    const result = await callable(input);
    return parseRaceTaxReadiness(result.data, input.raceId);
  }

  private async callAdminRaceTaxReview(name: string, input: { raceId: string }): Promise<AdminRaceTaxReview> {
    const callable = httpsCallable<typeof input, unknown>(functions, name);
    const result = await callable(input);
    return parseAdminRaceTaxReview(result.data, input.raceId);
  }

  async getAdminRaceTaxReview(input: { raceId: string }): Promise<AdminRaceTaxReview> {
    return this.callAdminRaceTaxReview("getAdminRaceTaxReview", input);
  }

  private async callOrganizerTaxSubmission(name: string, input: { raceId: string }, audience: "organizer" | "admin"): Promise<OrganizerRaceTaxSubmission> {
    const callable = httpsCallable<typeof input, unknown>(functions, name);
    const result = await callable(input);
    return parseOrganizerRaceTaxSubmission(result.data, input.raceId, audience);
  }

  async getOrganizerRaceTaxSubmission(input: { raceId: string }): Promise<OrganizerRaceTaxSubmission> {
    return this.callOrganizerTaxSubmission("getOrganizerRaceTaxSubmission", input, "organizer");
  }

  async listOrganizerRaceTaxSubmissions(input: { limit?: number } = {}): Promise<OrganizerTaxSubmissionInbox> {
    const callable = httpsCallable<typeof input, unknown>(functions, "listOrganizerRaceTaxSubmissions");
    const result = await callable(input);
    return parseOrganizerTaxSubmissionInbox(result.data);
  }

  async saveOrganizerRaceTaxSubmissionDraft(input: OrganizerTaxSubmissionMutation & { draft: OrganizerTaxSubmissionDraft }): Promise<OrganizerRaceTaxSubmission> {
    return this.callOrganizerTaxSubmission("saveOrganizerRaceTaxSubmissionDraft", input, "organizer");
  }

  async submitOrganizerRaceTaxSubmission(input: OrganizerTaxSubmissionMutation & { draft?: OrganizerTaxSubmissionDraft }): Promise<OrganizerRaceTaxSubmission> {
    return this.callOrganizerTaxSubmission("submitOrganizerRaceTaxSubmission", input, "organizer");
  }

  async getAdminRaceTaxSubmission(input: { raceId: string }): Promise<OrganizerRaceTaxSubmission> {
    return this.callOrganizerTaxSubmission("getAdminRaceTaxSubmission", input, "admin");
  }

  async reviewOrganizerRaceTaxSubmission(input: OrganizerTaxSubmissionMutation & { submissionId: string; decision: "reviewed" | "changes_requested"; reviewNotes: string }): Promise<OrganizerRaceTaxSubmission> {
    return this.callOrganizerTaxSubmission("reviewOrganizerRaceTaxSubmission", input, "admin");
  }

  async saveAdminRaceTaxReview(input: { raceId: string; expectedRevision: number; draft: AdminRaceTaxReviewDraft }): Promise<AdminRaceTaxReview> {
    return this.callAdminRaceTaxReview("saveAdminRaceTaxReview", input);
  }

  async saveAdminRaceTaxReviewSample(input: { raceId: string; expectedRevision: number; expectedSampleRevision: number; expectedContextFingerprint: string; billingAddress: TaxReviewAddress; expected: AdminRaceTaxReviewExpected }): Promise<AdminRaceTaxReview> {
    return this.callAdminRaceTaxReview("saveAdminRaceTaxReviewSample", input);
  }

  async runAdminRaceTaxReviewAudit(input: { raceId: string; expectedRevision: number; expectedContextFingerprint?: string; billingAddress: TaxReviewAddress; expected: AdminRaceTaxReviewExpected }): Promise<AdminRaceTaxReview> {
    return this.callAdminRaceTaxReview("runAdminRaceTaxReviewAudit", input);
  }

  async activateAdminRaceTaxReview(input: { raceId: string; expectedRevision: number; auditIds: string[] }): Promise<AdminRaceTaxReview> {
    return this.callAdminRaceTaxReview("activateAdminRaceTaxReview", input);
  }

  async revokeAdminRaceTaxReview(input: { raceId: string; expectedRevision: number }): Promise<AdminRaceTaxReview> {
    return this.callAdminRaceTaxReview("revokeAdminRaceTaxReview", input);
  }

  async acceptOrganizerPayoutTerms(): Promise<{ ok: boolean; version: string }> {
    const callable = httpsCallable<undefined, { ok: boolean; version: string }>(
      functions,
      "acceptOrganizerPayoutTerms"
    );
    const result = await callable(undefined);
    return result.data;
  }

  async inspectRacePayout(raceId: string): Promise<RacePayoutInspection> {
    const callable = httpsCallable<{ raceId: string }, unknown>(
      functions,
      "inspectRacePayout"
    );
    const result = await callable({ raceId });
    return parseRacePayoutInspection(result.data, raceId);
  }

  async releaseRacePayout(
    raceId: string,
    options: { repairOnly?: boolean } = {}
  ): Promise<RacePayoutReleaseResult> {
    const callable = httpsCallable<
      { raceId: string; repairOnly?: boolean },
      unknown
    >(
      functions,
      "releaseRacePayout"
    );
    const result = await callable({
      raceId,
      ...(options.repairOnly ? { repairOnly: true } : {}),
    });
    return parseRacePayoutReleaseResult(result.data, raceId);
  }

  async submitPaidRaceAccessApplication(
    input: PaidRaceApplicationInput
  ): Promise<{ status: PaidRaceAccessStatus }> {
    const callable = httpsCallable<
      PaidRaceApplicationInput,
      { status: PaidRaceAccessStatus }
    >(functions, "submitPaidRaceAccessApplication");
    const result = await callable(input);
    return result.data;
  }

  async reviewPaidRaceAccessApplication(input: {
    organizerUserId: string;
    decision: "approved" | "denied" | "reopened";
    reviewReason?: string;
  }): Promise<{ ok: boolean; organizerUserId: string; status: PaidRaceAccessStatus }> {
    const callable = httpsCallable<
      {
        organizerUserId: string;
        decision: "approved" | "denied" | "reopened";
        reviewReason?: string;
      },
      { ok: boolean; organizerUserId: string; status: PaidRaceAccessStatus }
    >(functions, "reviewPaidRaceAccessApplication");
    const result = await callable(input);
    return result.data;
  }

  async deleteCurrentAccount(): Promise<{
    ok: boolean;
    deletedStripeAccount: boolean;
    retainedStripeAccountForFinancialRecords?: boolean;
    storageCleanup?: {
      deletedProfilePhotoCount: number;
      deletedUserRaceGpxCount: number;
      scannedUserRaceCount: number;
    };
  }> {
    const callable = httpsCallable<
      undefined,
      {
        ok: boolean;
        deletedStripeAccount: boolean;
        retainedStripeAccountForFinancialRecords?: boolean;
        storageCleanup?: {
          deletedProfilePhotoCount: number;
          deletedUserRaceGpxCount: number;
          scannedUserRaceCount: number;
        };
      }
    >(functions, "deleteCurrentAccount");
    const result = await callable(undefined);
    return result.data;
  }

  async reviewRaceSubmission(input: {
    raceId: string;
    decision: "approved" | "denied";
    reviewNotes?: string;
  }): Promise<void> {
    const callable = httpsCallable<
      { raceId: string; decision: "approved" | "denied"; reviewNotes?: string; reviewRequestId: string },
      { ok: boolean }
    >(functions, "reviewRaceSubmission");

    const request = { ...input, reviewRequestId: crypto.randomUUID() };
    await retryRaceCallable(() => callable(request));
  }

  async getCurrentViewerTestRaceAccess(): Promise<{
    canAccessTestRaces: boolean;
    isAdmin: boolean;
  }> {
    const callable = httpsCallable<
      undefined,
      { canAccessTestRaces: boolean; isAdmin: boolean }
    >(functions, "getCurrentViewerTestRaceAccess");

    const result = await callable(undefined);
    return result.data;
  }

  async updateTestRaceAccessEmail(input: {
    action: "add" | "remove";
    email: string;
  }): Promise<{ emails: string[] }> {
    const callable = httpsCallable<
      { action: "add" | "remove"; email: string },
      { emails: string[] }
    >(functions, "updateTestRaceAccessEmail");

    const result = await callable(input);
    return result.data;
  }

  async getTestRaceDebugDocument(): Promise<TestRaceDebugDocumentResponse> {
    const callable = httpsCallable<undefined, TestRaceDebugDocumentResponse>(
      functions,
      "getTestRaceDebugDocument"
    );

    const result = await callable(undefined);
    return result.data;
  }

  async saveTestRaceDebugDocument(input: TestRaceDebugSaveInput): Promise<TestRaceDebugDocumentResponse> {
    const callable = httpsCallable<
      TestRaceDebugSaveInput,
      TestRaceDebugDocumentResponse
    >(functions, "saveTestRaceDebugDocument", { timeout: 540_000 });

    const result = await callable(input);
    return result.data;
  }

  async getRegisteredRacers(input: {
    raceId: string;
    pageSize?: number;
    cursor?: string;
    order?: ParticipantListOrder; locale?: string;
    category?: string;
  }): Promise<{ racers: RegisteredRacer[]; nextCursor: string | null; totalCount: number; categories: string[] }> {
    const callable = httpsCallable<
      { raceId: string; pageSize?: number; cursor?: string; order?: ParticipantListOrder; locale?: string; category?: string },
      { racers: RawRegisteredRacer[]; nextCursor?: string | null; totalCount?: number; categories?: string[] }
    >(functions, "getRegisteredRacers");

    const result = await callable(input);
    return {
      racers: result.data.racers.map(parseRegisteredRacer),
      nextCursor: result.data.nextCursor ?? null,
      totalCount: result.data.totalCount ?? result.data.racers.length,
      categories: result.data.categories ?? [],
    };
  }

  async searchRaceParticipants(input: {
    raceId: string;
    query: string;
    purpose?: "removal";
  }): Promise<{ participants: RaceParticipantSearchResult[] }> {
    const callable = httpsCallable<
      { raceId: string; query: string; purpose?: "removal" },
      { participants: RaceParticipantSearchResult[] }
    >(functions, "searchRaceParticipants");

    const result = await callable(input);
    return result.data;
  }

  async removeRaceParticipant(input: {
    raceId: string;
    participantUserId: string;
    registrationId: string | null;
  }): Promise<{ ok: boolean; participantUserId: string }> {
    const callable = httpsCallable<typeof input, { ok: boolean; participantUserId: string }>(functions, "removeRaceParticipant");
    const result = await callable(input);
    racerDataCache.clear();
    return result.data;
  }

  async disqualifyRaceParticipant(input: {
    raceId: string;
    participantUserId: string;
    reason: string;
  }): Promise<{
    ok: boolean;
    participantUserId: string;
    notificationStatus: RaceDisqualificationNotificationStatus;
  }> {
    const callable = httpsCallable<
      { raceId: string; participantUserId: string; reason: string },
      {
        ok: boolean;
        participantUserId: string;
        notificationStatus: RaceDisqualificationNotificationStatus;
      }
    >(functions, "disqualifyRaceParticipant");

    const result = await callable(input);
    return result.data;
  }

  async getPublicRacerProfile(input: {
    userId: string;
  }): Promise<{ profile: PublicRacerProfile }> {
    const callable = httpsCallable<
      { userId: string },
      { profile: RawPublicRacerProfile }
    >(functions, "getPublicRacerProfile");

    const result = await callable(input);
    return {
      profile: parsePublicRacerProfile(result.data.profile),
    };
  }

  async getPublicRacerRaces(input: { userId: string }): Promise<{ races: UserRace[] }> {
    const callable = httpsCallable<{ userId: string }, { races: unknown[] }>(functions, "getPublicRacerRaces");
    const result = await callable(input);
    return { races: result.data.races.map(race => parseUserRace(race)) };
  }

  async createRaceRegistrationCheckoutSession(input: {
    raceId: string;
    participantId?: string;
    pricingAcknowledgement: RegistrationPricingAcknowledgement;
    expectedPricing: RegistrationPricingSnapshot;
    userRace: UserRace;
    userProfile: {
      legalFirstName: string;
      legalLastName: string;
      email: string;
      legalAddress: string;
      city: string;
      state: string;
      country?: string;
      locale: LocaleCode;
      birthDate: Date;
      gender: string;
    };
  }): Promise<RaceRegistrationCheckoutResponse> {
    const callable = httpsCallable<
      {
        raceId: string;
        participantId?: string;
        pricingAcknowledgement: RegistrationPricingAcknowledgement;
        userRace: Record<string, unknown>;
        userProfile: {
          legalFirstName: string;
          legalLastName: string;
          email: string;
          legalAddress: string;
          city: string;
          state: string;
          country?: string;
          locale: LocaleCode;
          birthDate: string;
          gender: string;
        };
      },
      unknown
    >(functions, "createRaceRegistrationCheckoutSession");

    const { expectedPricing, ...callableInput } = input;
    const result = await callable({
      ...callableInput,
      userRace: serializeUserRaceForCallable(input.userRace),
      userProfile: {
        ...input.userProfile,
        birthDate: input.userProfile.birthDate.toISOString(),
      },
    });

    return parseRaceRegistrationCheckoutResponse(
      result.data,
      input.pricingAcknowledgement,
      expectedPricing
    );
  }

  async completeFreeRaceRegistration(input: {
    raceId: string;
    participantId?: string;
    userRace: UserRace;
    userProfile: {
      legalFirstName: string;
      legalLastName: string;
      email: string;
      legalAddress: string;
      city: string;
      state: string;
      country?: string;
      locale: LocaleCode;
      birthDate: Date;
      gender: string;
    };
  }): Promise<{ userRaceId: string; registrationId: string }> {
    const callable = httpsCallable<
      {
        raceId: string;
        participantId?: string;
        userRace: Record<string, unknown>;
        userProfile: {
          legalFirstName: string;
          legalLastName: string;
          email: string;
          legalAddress: string;
          city: string;
          state: string;
          country?: string;
          locale: LocaleCode;
          birthDate: string;
          gender: string;
        };
      },
      { userRaceId: string; registrationId: string }
    >(functions, "completeFreeRaceRegistration");

    const result = await callable({
      ...input,
      userRace: serializeUserRaceForCallable(input.userRace),
      userProfile: {
        ...input.userProfile,
        birthDate: input.userProfile.birthDate.toISOString(),
      },
    });

    return result.data;
  }

  async createRaceRegistrationPaymentIntent(input: {
    raceId: string;
    participantId?: string;
    pricingAcknowledgement: RegistrationPricingAcknowledgement;
    userRace: UserRace;
    userProfile: {
      legalFirstName: string;
      legalLastName: string;
      email: string;
      legalAddress: string;
      city: string;
      state: string;
      country?: string;
      locale: LocaleCode;
      birthDate: Date;
      gender: string;
    };
  }): Promise<{
    amount: number;
    clientSecret: string | null;
    currency: string;
    paymentIntentId: string;
    registrationId: string;
    livemode?: boolean;
    pricing: RegistrationPricingSnapshot;
  }> {
    const callable = httpsCallable<
      {
        raceId: string;
        participantId?: string;
        pricingAcknowledgement: RegistrationPricingAcknowledgement;
        userRace: Record<string, unknown>;
        userProfile: {
          legalFirstName: string;
          legalLastName: string;
          email: string;
          legalAddress: string;
          city: string;
          state: string;
          country?: string;
          locale: LocaleCode;
          birthDate: string;
          gender: string;
        };
      },
      {
        amount: number;
        clientSecret: string | null;
        currency: string;
        paymentIntentId: string;
        registrationId: string;
        livemode?: boolean;
        pricing: RegistrationPricingSnapshot;
      }
    >(functions, "createRaceRegistrationPaymentIntent");

    const result = await callable({
      ...input,
      userRace: serializeUserRaceForCallable(input.userRace),
      userProfile: {
        ...input.userProfile,
        birthDate: input.userProfile.birthDate.toISOString(),
      },
    });

    return result.data;
  }

  async resumeRaceRegistrationPaymentIntent(input: {
    registrationId: string;
    pricingAcknowledgement: RegistrationPricingAcknowledgement;
  }): Promise<{
    amount: number;
    clientSecret: string | null;
    currency: string;
    paymentIntentId: string;
    registrationId: string;
    livemode?: boolean;
    paymentIntentStatus: string;
    registrationStatus: string;
    pricing: RegistrationPricingSnapshot;
  }> {
    const callable = httpsCallable<
      {
        registrationId: string;
        pricingAcknowledgement: RegistrationPricingAcknowledgement;
      },
      {
        amount: number;
        clientSecret: string | null;
        currency: string;
        paymentIntentId: string;
        registrationId: string;
        livemode?: boolean;
        paymentIntentStatus: string;
        registrationStatus: string;
        pricing: RegistrationPricingSnapshot;
      }
    >(functions, "resumeRaceRegistrationPaymentIntent");

    const result = await callable(input);
    return result.data;
  }
}

export const functionsService = new FunctionsService();
